import React from 'react';
import styled from 'styled-components';

import { AllotmentStatus } from 'src/gql/graphql';
import type { AllotmentInfo } from 'src/gql/graphql';
import { ErrorExclamationV2 as ErrorIcon } from 'src/svg/error-exclamation-v2';
import { formatRemaining } from 'checkout/components/sections/mmur-allotment';

type MMURAlertBannerProps = {
  allotmentInfo: AllotmentInfo;
  allotmentCheckErrorMessage: string | null;
  shouldBlockOrder: boolean;
};

export default function MMURAlertBanner({
  allotmentInfo,
  allotmentCheckErrorMessage,
  shouldBlockOrder,
}: MMURAlertBannerProps): JSX.Element | null {
  const isExceededStatus = allotmentInfo.status === AllotmentStatus.exceeded;
  const exceededCategories =
    allotmentInfo.details?.filter((d) => d?.status === AllotmentStatus.exceeded && !d.isUnlimited) ?? [];

  const isExceeded = exceededCategories.length > 0 || isExceededStatus;
  const isError = allotmentInfo.status === AllotmentStatus.error;

  if (isExceeded) {
    return (
      <Banner role='alert' data-testid='mmur-alert-banner-exceeded'>
        <Header>
          <ErrorIcon />
          <Title data-testid='mmur-alert-title'>MMUR Limit Exceeded</Title>
        </Header>
        <Description data-testid='mmur-alert-description'>
          {shouldBlockOrder
            ? 'Product(s) in your cart exceed your MMUR limit. Please remove applicable items to proceed with your order.'
            : 'Product(s) in your cart exceed your MMUR limit. Some products may not be approved for purchase at in-store checkout.'}
        </Description>

        {exceededCategories.length > 0 && (
          <>
            <BannerDivider />
            <ExceededDetails>
              {exceededCategories.map(
                (detail) =>
                  detail && (
                    <span key={detail.category}>
                      {detail.category}:{' '}
                      <strong>{formatRemaining(detail.remainingThisCycle ?? 0, detail.unit ?? 'mg')} remaining</strong>
                    </span>
                  )
              )}
            </ExceededDetails>
          </>
        )}
      </Banner>
    );
  }

  if (isError) {
    return (
      <Banner role='alert' data-testid='mmur-alert-banner-error'>
        <Header>
          <ErrorIcon />
          <Title data-testid='mmur-alert-title'>MMUR Error</Title>
        </Header>
        <Description data-testid='mmur-alert-description'>
          {shouldBlockOrder
            ? 'MMUR allotment could not be verified. Please try again or contact the dispensary for assistance.'
            : 'MMUR allotment could not be verified. Purchasable items may be subject to change upon checkout at store.'}
        </Description>
        {allotmentCheckErrorMessage && (
          <>
            <BannerDivider />
            <Description data-testid='mmur-alert-error-detail'>{allotmentCheckErrorMessage}</Description>
          </>
        )}
      </Banner>
    );
  }

  return null;
}

const Banner = styled.div`
  background-color: #fae8ea;
  border-radius: 5px;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 28px 0 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.span`
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0.065px;
  color: #be0517;
`;

const Description = styled.p`
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0.065px;
  color: #be0517;
  margin: 0;
`;

const BannerDivider = styled.hr`
  border: none;
  border-top: 1px solid #f2b3b9;
  margin: 0;
  width: 100%;
`;

const ExceededDetails = styled.div`
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0.065px;
  color: #be0517;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
